import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip, Typography } from "antd";
import { PropsWithChildren, ReactElement, ReactNode, useId } from "react";
import styled from "styled-components";

export interface SettingPanelExpandedProps {
  title: string;
  tooltip?: ReactNode;
  icon: ReactNode;
  value?: string | number;
  showValue?: boolean;
}

export default function SettingPanelExpanded({ children, title, tooltip, icon, value, showValue }: PropsWithChildren<SettingPanelExpandedProps>): ReactElement {
  const titleId = useId();

  return (
    <SettingPanelExpandedBox>
      <HeaderBox>
        <Title id={titleId}>{title}</Title>
        {
          tooltip &&
          <Tooltip title={tooltip}>
            <HintIcon />
          </Tooltip>
        }
      </HeaderBox>
      <ContentRow aria-describedby={titleId}>
        <Icon aria-hidden>{icon}</Icon>
        <div>{children}</div>
        {
          showValue &&
          <Value>{value}</Value>
        }
      </ContentRow>
    </SettingPanelExpandedBox>
  );
}

const SettingPanelExpandedBox = styled.div`
  min-width: 200px;
  padding: ${p => p.theme.paddingXS}px;
  background-color: ${p => p.theme.controlItemBgHover};
  border-radius: ${p => p.theme.borderRadius}px;
`;

const HeaderBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.paddingXS}px;
`;

const Title = styled(Typography.Text)`
  flex: 0 1 auto;
  min-width: 0;
`;

const HintIcon = styled(InfoCircleOutlined)`
  font-size: ${p => p.theme.fontSizeSM}px;
  color: ${p => p.theme.colorTextSecondary};
  cursor: help;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.paddingSM}px;

  & > :nth-child(2) {
    flex: 1 1 auto;
    min-width: 0;

    display: flex;
    align-items: center;
  }
`;

const Icon = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  color: ${p => p.theme.colorTextSecondary};
`;

const Value = styled(Typography.Text)`
  min-width: 35px;
  text-align: right;
  user-select: none;
`;
